import { PublicKey } from '@solana/web3.js';
import bs58 from 'bs58';

// Types for Phantom wallet
type DisplayEncoding = 'utf8' | 'hex';

interface ConnectOpts {
  onlyIfTrusted: boolean;
}

export interface PhantomProvider {
  isPhantom?: boolean;
  publicKey: PublicKey | null;
  isConnected: boolean;
  signTransaction: (transaction: any) => Promise<any>;
  signAllTransactions: (transactions: any[]) => Promise<any[]>;
  signMessage: (
    message: Uint8Array | string,
    display?: DisplayEncoding
  ) => Promise<{ signature: Uint8Array; publicKey: PublicKey }>;
  connect: (opts?: Partial<ConnectOpts>) => Promise<{ publicKey: PublicKey }>;
  disconnect: () => Promise<void>;
  on: (event: string, handler: (args: any) => void) => void;
  removeListener: (event: string, handler: (args: any) => void) => void;
}

declare global {
  interface Window {
    solana?: PhantomProvider;
    phantom?: {
      solana?: PhantomProvider;
    };
  }
}

export class PhantomWalletError extends Error {
  constructor(message: string, public code?: number) {
    super(message);
    this.name = 'PhantomWalletError';
  }
}

export class PhantomWalletAdapter {
  private _provider: PhantomProvider | null = null;
  private _publicKey: PublicKey | null = null;
  private _connected = false;
  private _readyState: 'Loading' | 'NotDetected' | 'Installed' = 'Loading';
  private _onAccountChanged?: (newPublicKey: PublicKey | null, oldPublicKey: PublicKey | null) => void;
  private _onConnect?: (publicKey: PublicKey) => void;
  private _onDisconnect?: () => void;

  constructor() {
    this._handleConnect = this._handleConnect.bind(this);
    this._handleDisconnect = this._handleDisconnect.bind(this);
    this._handleAccountChanged = this._handleAccountChanged.bind(this);
    
    if (typeof window !== 'undefined') {
      this._checkProvider();
    }
  }

  private _checkProvider(): void {
    const provider = this.getProvider();
    if (provider) {
      this._provider = provider;
      this._readyState = 'Installed';
      
      // Set up event listeners
      this._provider.on('connect', this._handleConnect);
      this._provider.on('disconnect', this._handleDisconnect);
      this._provider.on('accountChanged', this._handleAccountChanged);
      
      // Check if already connected
      if (this._provider.isConnected && this._provider.publicKey) {
        this._publicKey = this._provider.publicKey;
        this._connected = true;
      }
    } else {
      this._readyState = 'NotDetected';
      
      // Check periodically for phantom to be loaded
      const checkInterval = setInterval(() => {
        const provider = this.getProvider();
        if (provider) {
          clearInterval(checkInterval);
          this._checkProvider();
        }
      }, 100);
      
      // Stop checking after 3 seconds
      setTimeout(() => clearInterval(checkInterval), 3000);
    }
  }

  private getProvider(): PhantomProvider | null {
    if (typeof window !== 'undefined') {
      // Check both window.phantom.solana and window.solana
      const provider = window.phantom?.solana || window.solana;
      if (provider?.isPhantom) {
        return provider;
      }
    }
    return null;
  }

  private _handleConnect(publicKey: PublicKey): void {
    console.log('Phantom wallet connected:', publicKey.toBase58());
    this._publicKey = publicKey;
    this._connected = true;
    this._onConnect?.(publicKey);
  }

  get provider(): PhantomProvider | null {
    return this._provider;
  }

  get publicKey(): PublicKey | null {
    return this._publicKey;
  }

  get connected(): boolean {
    return this._connected;
  }

  get isInstalled(): boolean {
    return this._readyState === 'Installed';
  }

  async connect(options?: { onlyIfTrusted?: boolean }): Promise<{ publicKey: PublicKey }> {
    if (this._readyState !== 'Installed' || !this._provider) {
      throw new PhantomWalletError(
        'Phantom wallet is not installed. Please install it from https://phantom.app',
        4001
      );
    }

    if (this._connected && this._publicKey) {
      return { publicKey: this._publicKey };
    }

    try {
      console.log('Attempting to connect to Phantom wallet...');
      const response = await this._provider.connect(options);
      console.log('Phantom wallet connected:', response);
      
      this._publicKey = response.publicKey;
      this._connected = true;

      return response;
    } catch (error: any) {
      console.error('Phantom wallet connection error:', error);
      if (error.code === 4001) {
        throw new PhantomWalletError('User rejected the connection request', 4001);
      }
      throw new PhantomWalletError(error.message || 'Failed to connect to Phantom wallet');
    }
  }

  async disconnect(): Promise<void> {
    if (this._provider && this._connected) {
      try {
        await this._provider.disconnect();
      } catch (error) {
        console.warn('Error during disconnect:', error);
      }
    }
    this._handleDisconnect();
  }

  async signMessage(message: string): Promise<{ signature: Uint8Array; publicKey: PublicKey }> {
    if (!this._provider || !this._connected) {
      throw new PhantomWalletError('Wallet not connected');
    }

    if (!this._publicKey) {
      throw new PhantomWalletError('No public key available');
    }

    try {
      // Convert string message to Uint8Array
      const encodedMessage = new TextEncoder().encode(message);
      const result = await this._provider.signMessage(encodedMessage, 'utf8');
      
      return {
        signature: result.signature,
        publicKey: this._publicKey
      };
    } catch (error: any) {
      if (error.code === 4001) {
        throw new PhantomWalletError('User rejected the signing request', 4001);
      }
      throw new PhantomWalletError(error.message || 'Failed to sign message');
    }
  }

  private _handleDisconnect(): void {
    const oldPublicKey = this._publicKey;
    this._publicKey = null;
    this._connected = false;
    
    console.log('Phantom wallet disconnected');
    this._onDisconnect?.();
    
    // Trigger account changed event with null to indicate disconnection
    if (oldPublicKey) {
      this._onAccountChanged?.(null, oldPublicKey);
    }
  }

  private _handleAccountChanged(publicKey: PublicKey | null): void {
    const oldPublicKey = this._publicKey;
    
    // Skip if this is the same account (prevent duplicate events)
    if (oldPublicKey && publicKey && oldPublicKey.toBase58() === publicKey.toBase58()) {
      return;
    }
    
    console.log('Phantom account changed:', {
      old: oldPublicKey?.toBase58(),
      new: publicKey?.toBase58()
    });
    
    if (publicKey) {
      // Account switched to a different account
      this._publicKey = publicKey;
      this._connected = true;
      
      // Only trigger callback if it's a real change
      if (this._onAccountChanged) {
        this._onAccountChanged(publicKey, oldPublicKey);
      }
    } else {
      // Account disconnected or no longer available
      this._publicKey = null;
      this._connected = false;
      
      // Trigger account change with null
      if (this._onAccountChanged) {
        this._onAccountChanged(null, oldPublicKey);
      }
    }
  }

  // Helper method to create authentication message
  createAuthMessage(nonce: string): string {
    const timestamp = new Date().toISOString();
    return `Welcome to sphere.fun!\n\nThis request will not trigger a blockchain transaction or cost any gas fees.\n\nNonce: ${nonce}\nTimestamp: ${timestamp}`;
  }

  // Convert signature to base58 string for backend
  signatureToBase58(signature: Uint8Array): string {
    // Convert Uint8Array to base58 string
    return bs58.encode(signature);
  }

  // Set account change callback
  setAccountChangeCallback(callback: (newPublicKey: PublicKey | null, oldPublicKey: PublicKey | null) => void): void {
    this._onAccountChanged = callback;
  }

  // Remove account change callback
  removeAccountChangeCallback(): void {
    this._onAccountChanged = undefined;
  }

  // Set connect callback
  setConnectCallback(callback: (publicKey: PublicKey) => void): void {
    this._onConnect = callback;
  }

  // Remove connect callback
  removeConnectCallback(): void {
    this._onConnect = undefined;
  }

  // Set disconnect callback
  setDisconnectCallback(callback: () => void): void {
    this._onDisconnect = callback;
  }

  // Remove disconnect callback
  removeDisconnectCallback(): void {
    this._onDisconnect = undefined;
  }

  // Check if this is a real account switch (not initial connection)
  isAccountSwitch(newPublicKey: PublicKey | null, oldPublicKey: PublicKey | null): boolean {
    // It's an account switch if:
    // 1. Both old and new keys exist and are different, OR
    // 2. Old key exists but new key is null (disconnection), OR
    // 3. Old key is null but new key exists AND we were previously connected (edge case)
    
    if (oldPublicKey && newPublicKey) {
      return oldPublicKey.toBase58() !== newPublicKey.toBase58();
    }
    
    if (oldPublicKey && !newPublicKey) {
      return true; // Disconnection
    }
    
    // If we had a previous connection state but now have a different account
    if (!oldPublicKey && newPublicKey && this._connected) {
      return true;
    }
    
    return false;
  }

  // Debug method to check provider state
  getDebugInfo(): any {
    return {
      readyState: this._readyState,
      hasProvider: !!this._provider,
      connected: this._connected,
      publicKey: this._publicKey?.toBase58(),
      windowSolana: !!(window as any).solana,
      windowPhantom: !!(window as any).phantom,
      isPhantom: (window as any).solana?.isPhantom || (window as any).phantom?.solana?.isPhantom
    };
  }
}

// Singleton instance
export const phantomWallet = new PhantomWalletAdapter(); 